import { Address, AddressExtended } from '@dadohr/shared/domainTypes/address';
import { TextField } from '@material-ui/core';
import { omit } from 'lodash';
import { useMemo } from 'react';

import { useUnsafeTranslation } from '../../i18next';
import { CustomInputLabel } from '../CustomInputLabel';
import { FormRow } from '../Form/FormRow';

type ExtendedExclusionField = 'name' | 'phoneNumber' | 'email';

export const AddressExtendedForm = ({
  value,
  onChange,
  variant = 'normal',
  disabled,
  autoFocus,
  excludedFields,
  required
}: {
  value: AddressExtended;
  onChange: (nextValue: AddressExtended) => void;
  variant?: 'normal' | 'compact';
  disabled?: boolean;
  autoFocus?: boolean;
  excludedFields?: ExtendedExclusionField[];
  required?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const sharedProps = useMemo(
    () => ({
      type: 'text',
      fullWidth: true,
      disabled,
      InputProps: {
        readOnly: disabled
      }
    }),
    [disabled]
  );

  // Postal Code, City, State/Region, Country
  if (variant === 'compact') {
    return (
      <>
        {!excludedFields?.includes('name') && (
          <FormRow>
            <TextField
              label={t('Name')}
              value={value.name}
              required={required}
              onChange={(ev) => onChange({ ...value, name: ev.target.value })}
              autoFocus={autoFocus}
              autoComplete="name"
              {...sharedProps}
            />
          </FormRow>
        )}
        <FormRow>
          <TextField
            label={t('Street Address 1')}
            value={value.addressLine1}
            required={required}
            onChange={(ev) =>
              onChange({ ...value, addressLine1: ev.target.value })
            }
            autoComplete="address-line1"
            {...sharedProps}
          />
        </FormRow>
        <FormRow>
          <TextField
            label={t('Street Address 2')}
            value={value.addressLine2}
            onChange={(ev) =>
              onChange({ ...value, addressLine2: ev.target.value })
            }
            autoComplete="address-line2"
            {...sharedProps}
          />
        </FormRow>

        <FormRow>
          <TextField
            label={t('Postal Code')}
            value={value.postalCode}
            required={required}
            onChange={(ev) =>
              onChange({ ...value, postalCode: ev.target.value })
            }
            autoComplete="postal-code"
            {...sharedProps}
          />
          <TextField
            label={t('City')}
            value={value.city}
            required={required}
            onChange={(ev) => onChange({ ...value, city: ev.target.value })}
            autoComplete="address-level2"
            {...sharedProps}
          />
        </FormRow>

        <FormRow>
          <TextField
            label={t('State/Region')}
            value={value.region}
            required={required}
            onChange={(ev) => onChange({ ...value, region: ev.target.value })}
            autoComplete="address-level1"
            {...sharedProps}
          />
          <TextField
            label={t('Country')}
            value={value.country}
            required={required}
            onChange={(ev) => onChange({ ...value, country: ev.target.value })}
            autoComplete="country"
            {...sharedProps}
          />
        </FormRow>
      </>
    );
  }
  return (
    <>
      {!excludedFields?.includes('name') && (
        <FormRow>
          <CustomInputLabel fullWidth label={t('Name')}>
            <TextField
              value={value.name}
              required={required}
              onChange={(ev) => onChange({ ...value, name: ev.target.value })}
              autoComplete="name"
              {...sharedProps}
            />
          </CustomInputLabel>
        </FormRow>
      )}
      <FormRow>
        <CustomInputLabel fullWidth label={t('Street Address 1')}>
          <TextField
            value={value.addressLine1}
            required={required}
            onChange={(ev) =>
              onChange({ ...value, addressLine1: ev.target.value })
            }
            autoComplete="address-line1"
            {...sharedProps}
          />
        </CustomInputLabel>
      </FormRow>
      <FormRow>
        <CustomInputLabel fullWidth label={t('Street Address 2')}>
          <TextField
            value={value.addressLine2}
            required={required}
            onChange={(ev) =>
              onChange({ ...value, addressLine2: ev.target.value })
            }
            autoComplete="address-line2"
            {...sharedProps}
          />
        </CustomInputLabel>
      </FormRow>

      <FormRow>
        <CustomInputLabel fullWidth label={t('Postal Code')}>
          <TextField
            value={value.postalCode}
            required={required}
            onChange={(ev) =>
              onChange({ ...value, postalCode: ev.target.value })
            }
            autoComplete="postal-code"
            {...sharedProps}
          />
        </CustomInputLabel>
        <CustomInputLabel fullWidth label={t('City')}>
          <TextField
            value={value.city}
            required={required}
            onChange={(ev) => onChange({ ...value, city: ev.target.value })}
            autoComplete="address-level2"
            {...sharedProps}
          />
        </CustomInputLabel>
      </FormRow>

      <FormRow>
        <CustomInputLabel fullWidth label={t('State/Region')}>
          <TextField
            value={value.region}
            required={required}
            onChange={(ev) => onChange({ ...value, region: ev.target.value })}
            autoComplete="address-level1"
            {...sharedProps}
          />
        </CustomInputLabel>
        <CustomInputLabel fullWidth label={t('Country')}>
          <TextField
            value={value.country}
            required={required}
            onChange={(ev) => onChange({ ...value, country: ev.target.value })}
            autoComplete="country"
            {...sharedProps}
          />
        </CustomInputLabel>
      </FormRow>

      {(!excludedFields?.includes('phoneNumber') ||
        !excludedFields?.includes('email')) && (
        <FormRow>
          {!excludedFields?.includes('phoneNumber') && (
            <CustomInputLabel fullWidth label={t('Phone Number')}>
              <TextField
                value={value.phoneNumber}
                required={required}
                onChange={(ev) =>
                  onChange({ ...value, phoneNumber: ev.target.value })
                }
                autoComplete="tel"
                {...sharedProps}
              />
            </CustomInputLabel>
          )}
          {!excludedFields?.includes('email') && (
            <CustomInputLabel fullWidth label={t('Email Address')}>
              <TextField
                value={value.email}
                required={required}
                onChange={(ev) =>
                  onChange({ ...value, email: ev.target.value })
                }
                autoComplete="email"
                {...sharedProps}
              />
            </CustomInputLabel>
          )}
        </FormRow>
      )}
    </>
  );
};

export const AddressForm = ({
  value,
  onChange,
  variant = 'normal',
  disabled,
  autoFocus,
  excludedFields,
  required
}: {
  value: Address;
  onChange: (nextValue: Address) => void;
  variant?: 'normal' | 'compact';
  disabled?: boolean;
  autoFocus?: boolean;
  excludedFields?: 'name'[];
  required?: boolean;
}) => {
  const extendedExcludedFields: ExtendedExclusionField[] = useMemo(() => {
    return [...(excludedFields || []), 'phoneNumber', 'email'];
  }, [excludedFields]);

  const extendedValue: AddressExtended = useMemo(() => {
    return {
      ...value,
      phoneNumber: '',
      email: ''
    };
  }, [value]);

  return (
    <AddressExtendedForm
      value={extendedValue}
      onChange={(nextExtendedValue) =>
        onChange(omit(nextExtendedValue, ['phoneNumber', 'email']))
      }
      variant={variant}
      disabled={disabled}
      autoFocus={autoFocus}
      excludedFields={extendedExcludedFields}
      required={required}
    />
  );
};
